
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Users, Shield } from 'lucide-react';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';

interface Employee {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role?: 'employee' | 'manager' | 'admin';
}

export const RoleManagement: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchEmployeesWithRoles();
  }, []);

  const fetchEmployeesWithRoles = async () => {
    try {
      setLoading(true);

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { data, error } = await supabase.rpc('pin_list_profiles', { p_pin: pin });
        if (error) throw error;

        const employeesWithRoles: Employee[] = ((data as any[]) || [])
          .filter((p: any) => p && (typeof p.is_active !== 'boolean' || p.is_active === true))
          .map((p: any) => {
            const appRole = (p.app_role as string | null) ?? 'employe';
            const role: Employee['role'] =
              appRole === 'admin' ? 'admin' : appRole === 'manager' || appRole === 'contremaitre' ? 'manager' : 'employee';
            return {
              id: p.id,
              first_name: p.first_name ?? null,
              last_name: p.last_name ?? null,
              email: p.email ?? null,
              role,
            };
          });

        setEmployees(employeesWithRoles);
        return;
      }

      // Fallback JWT
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase.from('profiles').select('id, first_name, last_name, email').eq('is_active', true);
      if (profilesError) throw profilesError;

      // Fetch all user roles
      const { data: roles, error: rolesError } = await supabase.from('user_roles').select('user_id, role');
      if (rolesError) throw rolesError;

      // Combine profiles with roles
      const employeesWithRoles =
        profiles?.map((profile) => ({
          ...profile,
          role: roles?.find((role) => role.user_id === profile.id)?.role || 'employee',
        })) || [];

      setEmployees(employeesWithRoles as any);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les employés",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: 'employee' | 'manager' | 'admin') => {
    try {
      setUpdating(userId);

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const mappedAppRole = newRole === 'admin' ? 'admin' : newRole === 'manager' ? 'manager' : 'employe';
        const { error } = await supabase.rpc('pin_update_profile', {
          p_pin: pin,
          p_profile_id: userId,
          p_app_role: mappedAppRole,
        });
        if (error) throw error;
      } else {
        // Delete existing role
        await supabase.from('user_roles').delete().eq('user_id', userId);

        // Insert new role
        const { error } = await supabase.from('user_roles').insert([{ user_id: userId, role: newRole }]);
        if (error) throw error;
      }

      // Update local state
      setEmployees(prev => prev.map(emp => 
        emp.id === userId ? { ...emp, role: newRole } : emp
      ));

      toast({
        title: "Succès",
        description: "Rôle mis à jour avec succès",
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du rôle:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le rôle",
        variant: "destructive",
      });
    } finally {
      setUpdating(null);
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'manager':
        return 'default';
      case 'employee':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getEmployeeName = (employee: Employee) => {
    return `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.email || 'Nom inconnu';
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-lg">Chargement des employés...</div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Gestion des Rôles
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {employees.map((employee) => (
            <div
              key={employee.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="flex items-center gap-3">
                <Users className="h-8 w-8 text-gray-400" />
                <div>
                  <div className="font-medium">{getEmployeeName(employee)}</div>
                  <div className="text-sm text-muted-foreground">{employee.email}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Badge variant={getRoleBadgeVariant(employee.role || 'employee')}>
                  {employee.role === 'admin' && 'Administrateur'}
                  {employee.role === 'manager' && 'Manager'}
                  {employee.role === 'employee' && 'Employé'}
                </Badge>
                
                <Select
                  value={employee.role || 'employee'}
                  onValueChange={(value) => updateUserRole(employee.id, value as 'employee' | 'manager' | 'admin')}
                  disabled={updating === employee.id}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employé</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>

        {employees.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            Aucun employé trouvé
          </div>
        )}
      </CardContent>
    </Card>
  );
};
